import React, { useEffect, useState } from "react";

import { StateLanguage } from "../../languages/config/StateLanguage";
import { SettingsDA } from "../../services/admin/settings.service";
import { Error } from "../../models/error.model";
import { MeasureUnit } from "../../models/settings.measure-units.model";
import { GlobalSwal } from "../../styles/swal.style";
import { legendInvalid, legendIconInvalid, legendIconValidInvalidRestart, legendValidInvalidRestart } from "../../utils/legend.data.util";
import { modalHide } from "../../utils/modal.util";
import { evaluateLegendRequiredIcon } from "../../scripts/validate.legend.script";
import { expressions } from "../../libraries/regular.expressions.library";
import { getError, getMessage } from "../../languages/translations/response";
import { errorWebHandler } from "../../scripts/error.web.handler.script";
import { Company } from "../../models/company.model";

import AppLegend from "../../components/element/Legend";
import AppInputUnitType from "../../components/element/InputUnitType";

export interface AppModalSettingsMeasureUnitProps {
  companyForUser: Company | null;
  unitItem: MeasureUnit | null;
  onSuccess: (mode: "create" | "edit") => void;
}

let errorResponse: Error;

const AppModalSettingsMeasureUnit: React.FunctionComponent<AppModalSettingsMeasureUnitProps> = ({
  companyForUser,
  unitItem,
  onSuccess,
}) => {
    const { lang } = StateLanguage();

    const [loadIndicator, setLoadIndicator] = useState("off");
    const [type, setType] = useState({ value: "", valid: false });
    const [name, setName] = useState({ value: "", valid: false });
    const [symbol, setSymbol] = useState({ value: "", valid: false });

    const isEditMode = unitItem !== null;

    const submitMeasureUnit = async () => {
        setLoadIndicator("on");

        if (companyForUser && type.valid && name.valid && symbol.valid) {
            const request = isEditMode
                ? SettingsDA.putSettingMeasureUnit(companyForUser.id, unitItem!.id, type.value, name.value, symbol.value)
                : SettingsDA.postSettingMeasureUnit(companyForUser.id, type.value, name.value, symbol.value);

            await request
                .then((response) => {
                    if (response.status === 200 || response.status === 201) {
                      const mode = isEditMode ? "edit" : "create";
                      onSuccess(mode);

                        GlobalSwal.fire({
                            title: isEditMode
                                ? getMessage("Successfully updated measure unit", lang.code)
                                : getMessage("Successfully created measure unit", lang.code),
                            text: lang.labels.actionCompletedReturningToPage,
                            icon: "success",
                            showConfirmButton: false,
                            timer: 2200,
                        }).then(() => {
                            executeHideModal();
                        });
                    } else {
                        errorResponse = response.data;

                        GlobalSwal.fire({
                            title: getError(errorResponse.code, lang.code, errorResponse.message),
                            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                            icon: "error",
                            confirmButtonText: lang.labels.okGotIt,
                        });
                    }
                })
                .catch((error) => {
                    errorWebHandler(lang, error);
                })
                .finally(() => {
                    setLoadIndicator("off");
                });
        } else {
            GlobalSwal.fire({
                text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
                icon: "error",
                showConfirmButton: false,
                timer: 2200,
            }).then(() => {
                if (!type.value.length) legendInvalid("legend-unit-type-required");
                if (!name.value.length)
                    legendIconInvalid("modal-settings-measure-unit-input-name", "modal-settings-measure-unit-legend-name-required");
                if (!symbol.value.length)
                    legendIconInvalid("modal-settings-measure-unit-input-symbol", "modal-settings-measure-unit-legend-symbol-required");
                setLoadIndicator("off");
            });
        }
    };

    const handleChangeName = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
        setName({ ...name, value: event.target.value });
    };

    const handleChangeSymbol = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
        setSymbol({ ...symbol, value: event.target.value });
    };

    const validateName = () => {
        evaluateLegendRequiredIcon(
            expressions.text,
            name,
            setName,
            "modal-settings-measure-unit-input-name",
            "modal-settings-measure-unit-legend-name-valid",
            "modal-settings-measure-unit-legend-name-required",
        );
    };

    const validateSymbol = () => {
        evaluateLegendRequiredIcon(
            expressions.text,
            symbol,
            setSymbol,
            "modal-settings-measure-unit-input-symbol",
            "modal-settings-measure-unit-legend-symbol-valid",
            "modal-settings-measure-unit-legend-symbol-required",
        );
    };

    const executeHideModal = () => {
        modalHide("modal-settings-measure-unit");

        setTimeout(() => {
            restartModal();
        }, 200);
    };

    function restartModal() {
        legendValidInvalidRestart("legend-unit-type-required");
        legendIconValidInvalidRestart("modal-settings-measure-unit-input-name", "modal-settings-measure-unit-legend-name-valid");
        legendIconValidInvalidRestart("modal-settings-measure-unit-input-name", "modal-settings-measure-unit-legend-name-required");
        legendIconValidInvalidRestart("modal-settings-measure-unit-input-symbol", "modal-settings-measure-unit-legend-symbol-valid");
        legendIconValidInvalidRestart("modal-settings-measure-unit-input-symbol", "modal-settings-measure-unit-legend-symbol-required");

        setType({ value: "", valid: false });
        setName({ value: "", valid: false });
        setSymbol({ value: "", valid: false });
    }

    useEffect(() => {
        if (unitItem) {
            setType({ value: unitItem.type, valid: true });
            setName({ value: unitItem.name, valid: true });
            setSymbol({ value: unitItem.symbol, valid: true });
        } else {
            restartModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitItem]);

    return (
        <div
            id="modal-settings-measure-unit"
            className="modal fade"
            tabIndex={-1}
            aria-hidden="true"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
        >
            <div className="modal-dialog modal-md">
                <div className="modal-content">
                    <div className="modal-header">
                        <h2 className="text-capitalize">
                            {isEditMode ? lang.labels.editMeasureUnitParameter : lang.labels.addMeasureUnitParameter}
                        </h2>
                    </div>
                    <div className="modal-body">
                        <div className="form">
                            <div className="row mb-2">
                                <label className="col-lg-3 col-form-label required">{lang.labels.type}</label>
                                <div className="col-lg-9">
                                    <AppInputUnitType options={null} typeUnit={type} setTypeUnit={setType} />
                                </div>
                            </div>
                            <div className="row mb-2">
                                <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
                                <div className="col-lg-9">
                                    <input
                                        id="modal-settings-measure-unit-input-name"
                                        className="form-control"
                                        type="text"
                                        autoComplete="off"
                                        name="name"
                                        value={name.value}
                                        onChange={handleChangeName}
                                        onKeyUp={validateName}
                                        onBlur={validateName}
                                    />
                                    <AppLegend
                                        component={"modal-settings-measure-unit"}
                                        attribute={{ check: name.valid, name: "name", index: null, sub_index: null }}
                                        container={{ valid: true, required: true, identical: false, exists: false }}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <label className="col-lg-3 col-form-label required">{lang.labels.symbol}</label>
                                <div className="col-lg-9">
                                    <input
                                        id="modal-settings-measure-unit-input-symbol"
                                        className="form-control"
                                        type="text"
                                        autoComplete="off"
                                        name="symbol"
                                        value={symbol.value}
                                        onChange={handleChangeSymbol}
                                        onKeyUp={validateSymbol}
                                        onBlur={validateSymbol}
                                    />
                                    <AppLegend
                                        component={"modal-settings-measure-unit"}
                                        attribute={{ check: symbol.valid, name: "symbol", index: null, sub_index: null }}
                                        container={{ valid: true, required: true, identical: false, exists: false }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer flex-center">
                        <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>
                            {lang.labels.discard}
                        </button>
                        <button
                            className="btn btn-primary mx-2"
                            type="button"
                            data-kt-indicator={loadIndicator}
                            onClick={submitMeasureUnit}
                        >
                            <span className="indicator-label">{isEditMode ? lang.labels.saveChanges : lang.labels.create}</span>
                            <span className="indicator-progress">
                                {lang.labels.pleaseWait}
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppModalSettingsMeasureUnit;
